import { useEffect, useState } from "react";
import {
  Flex,
  InputGroup,
  Spacer,
  IconButton,
  Switch,
  Stack,
  Text,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { Input, FormModal } from "@/components";
import { useIntegration, useSettings } from "@/hooks";
import { SellerWebhooks } from "@/models";
import { copyTextToClipboard,showToast } from "@/utils";
import { EditIcon } from "@/styles/icons/EditIcon";
import { TrashIcon } from "@/styles/icons/TrashIcon";
import { Alert, Checkbox, Select  } from "barte-design-system";
import { DuplicateIcon } from "@/styles/icons";
import { ChargeDomainField } from "./_charge-domain-field";
import { buildWebhookDomains } from "./_webhook-domains";
interface WebhookItemProps {
  webhook: SellerWebhooks;
}

const webhookVersionOptions = [
  { label: "v1", value: "1.0" },
  { label: "v2", value: "2.0" },
];

const webhookCreateScheama = yup.object().shape({
  title: yup.string().required("Preenchimento obrigatório!"),
  url: yup
    .string()
    .matches(
      /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/,
      "Entre com uma url correta"
    )
    .required("Preenchimento obrigatório!"),
  version: yup.string(),
  subscriptions: yup.boolean(),
  order: yup.boolean(),
  charge: yup.boolean(),
});

export const WebhookItem = ({ webhook }: WebhookItemProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { handleModal, isLoading, setIsLoading } = useIntegration();
  const { putSellerApiWebhook } = useSettings();
  const {
    getWebhooksInitialData,
  } = useIntegration();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(webhookCreateScheama),
  });

  useEffect(() => {
    if (isEditModalOpen) {
      setValue("title", webhook.title);
      setValue("url", webhook.url);
      setValue("version", webhook.version ?? "1.0");
      setValue("subscriptions", webhook.domains.includes("SUBSCRIPTION"));
      setValue("order", webhook.domains.includes("ORDER"));
      setValue("charge", webhook.domains.includes("CHARGE"));
    }
  }, [isEditModalOpen, webhook, setValue]);
  const getTitleValue = watch("title");
  const getUrlValue = watch("url");
  const getVersionValue = watch("version");
  const getSubscriptionsValue = watch("subscriptions");
  const getOrderValue = watch("order");
  const getChargeValue = watch("charge");
  const selectedDomains = buildWebhookDomains({
    subscriptions: getSubscriptionsValue,
    order: getOrderValue,
    charge: getChargeValue,
    version: getVersionValue,
  });
  const onSubmit = handleSubmit(
    async (
      data: Pick<
        SellerWebhooks,
        "title" | "url" | "version" | "subscriptions" | "order" | "charge"
      >
    ) => {
      try {
        setIsLoading(true);
        const domains = buildWebhookDomains(data);
        await putSellerApiWebhook(webhook.uuid, {
          title: data.title,
          url: data.url,
          version: data.version,
          domains,
          active: webhook.active,
        });
        getWebhooksInitialData();
        showToast({
          message: "Webhook atualizado com sucesso!",
          type: "success",
        });
      } catch (error) {
        error?.response?.data?.errors.map((error) => {
          showToast({
            type: "error",
            message: `${error?.code} - ${error?.description}`
          })
        });
      } finally {
        setIsEditModalOpen(!isEditModalOpen);
        setIsLoading(false);
      }
    }
  );

  return (
    <>
      <FormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(!isEditModalOpen);
        }}
        title="Editar Webhook"
        handleConfirmModal={onSubmit}
        isSubmitting={isLoading}
        confirmationButtonText="Salvar"
        disableWhenIsFormEmpty={
          !getTitleValue || !getUrlValue || selectedDomains.length === 0
        }
      >
        <Stack spacing={4}>
          <Alert
            title="Atenção à versão dos eventos"
            variant="warning"
            description="Ao alterar a versão, apenas os eventos de Cobrança Pontual passam a ser enviados no formato v2. Os eventos de Assinaturas permanecem em v1 por enquanto."
            showCloseButton={false}
          />
          <Input
            type="text"
            label="Nome"
            name="title"
            register={{ ...register("title") }}
            errors={errors?.title}
          />
          <Input
            type="text"
            label="Url"
            name="url"
            register={{ ...register("url") }}
            errors={errors?.url}
          />
          <Select
            label="Versão"
            options={webhookVersionOptions}
            value={getVersionValue ?? "1.0"}
            onChange={(value) => {
              setValue("version", value, { shouldValidate: true });
              if (value === "1.0") {
                setValue("charge", false);
              }
            }}
          />
          <Text color="gray.400" fontSize=".8rem">
            Domínios
          </Text>
          <Checkbox
            name="subscriptions"
            defaultChecked={webhook.domains.includes("SUBSCRIPTION")}
            {...register("subscriptions")}
            label="Assinaturas"
            onChange={() => {}}
          />
          <Checkbox
            name="orders"
            defaultChecked={webhook.domains.includes("ORDER")}
            {...register("order")}
            label="Cobrança Pontual"
            onChange={() => { }}
          />
          <ChargeDomainField
            isVersion2={getVersionValue === "2.0"}
            isChecked={!!getChargeValue}
            onChange={(checked) => setValue("charge", checked)}
          />
        </Stack>
      </FormModal>

      <Flex
        w="100%"
        bgColor="gray.25"
        p="8px"
        align="center"
        gap={6}
        borderRadius="4px"
      >
        <InputGroup maxW="280px">
       
          <Text>
            {webhook.title}
          </Text>
        </InputGroup>
        <Spacer />
        <Flex
          maxW="360px"
          w="100%"
        >
          <Input
            name="textUrl"
            value={webhook.url}
            readOnly

          />
        </Flex>
        <Flex direction="column" minW="72px">
          <Text fontSize="12px" color="gray.400">
            Versão
          </Text>
          <Text fontSize="14px" fontWeight="medium">
            {webhook.version ?? "-"}
          </Text>
        </Flex>
        <IconButton
            aria-label="copy to clipboard"
            bgColor="transparent"
            icon={<DuplicateIcon />}
            onClick={() =>
                copyTextToClipboard(
                    webhook.url,
                    "Token copiado para a área de transferência!"
                  )
            }
            _hover={{
              cursor: "pointer",
            }}
          />
        <IconButton
          aria-label="edit title"
          bgColor="transparent"
          icon={<EditIcon />}
          onClick={() => {
            setIsEditModalOpen(!isEditModalOpen);
          }}
          _hover={{
            bgColor: "transparent",
          }}
        />
        <IconButton
          aria-label="delete token"
          bgColor="transparent"
          icon={<TrashIcon />}
          onClick={() => handleModal({ type: "deleteWebhook", webhook })}
          _hover={{
            bgColor: "transparent",
          }}
        />
        <Switch
          isChecked={webhook.active}
          onChange={() => handleModal({ type: "changeActiveWebhook", webhook })}
        />
      </Flex>
    </>
  );
};
